/**
 * @graphql cc
 */
import { useEffect, useRef } from 'react';

import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation/pm-plugins/types';

import {
	handleDeepLinkNavigation,
	NavigationContext,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

type FocusedCommentProps = {
	commentId: string | undefined;
	contentId: string;
	annotationState: InlineCommentPluginState | undefined;
	handleGqlError: (error: Error) => void;
};

// Using common utility function from comments-util

export function useFocusedComment({
	commentId,
	contentId,
	annotationState,
	handleGqlError,
}: FocusedCommentProps): void {
	const commentDataResult = useCommentById({
		commentId: commentId || '',
		contentId,
		options: {
			skip: !commentId,
			onError: handleGqlError,
		},
	});

	const { setCurrentlySelectedThreadKey, setCommentContainerToFocus } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const hasOpenedFocusedComment = useRef(false);

	// Reset hasOpenedFocusedComment when commentId changes
	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			hasOpenedFocusedComment.current = false;
		}
	}, [commentId]);

	useEffect(() => {
		if (hasOpenedFocusedComment.current) {
			return;
		}
		const focusedComment = commentDataResult.comment ?? null;

		if (commentId && focusedComment) {
			const markerRef = focusedComment.location?.inlineMarkerRef ?? '';
			const isResolved = Boolean(focusedComment.location?.inlineResolveProperties?.resolved);

			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });

				if (markerRef) {
					setCurrentlySelectedThreadKey(markerRef);
					setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
					hasOpenedFocusedComment.current = true;
				}
			} else if (
				// dangling comment
				markerRef &&
				annotationState?.annotationsLoaded &&
				// On live docs annotationsLoaded can flip to true before the collab
				// provider has synced the document, so `annotations` is briefly {}
				// and a valid markerRef gets misclassified as dangling. Requiring
				// at least one annotation closes that race; truly dangling cases
				// always have other annotations present in the document.
				// See bug 19 in https://hello.atlassian.net/wiki/spaces/SCE/pages/7070032438
				Object.keys(annotationState.annotations).length > 0 &&
				!(markerRef in annotationState.annotations)
			) {
				setCurrentlySelectedThreadKey(markerRef);
				setCommentContainerToFocus(commentId); // we'll be using this to scroll to it using data-comment-id
				showCommentsPanel({
					openWithView: expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true)
						? ViewValues.OPEN
						: ViewValues.DELETED_CONTENT,
				});
				hasOpenedFocusedComment.current = true; // to prevent focused comment from being opened again. We may need a better way to handle this
			} else {
				const commentElement = document.getElementById(markerRef);

				if (commentElement && markerRef !== '') {
					// Use unified navigation for deep linking
					const success = handleDeepLinkNavigation({
						scrollBehavior: ScrollBehavior.INSTANT,
						context: NavigationContext.EDITOR,
						block: 'center',
						inline: 'nearest',
						markElement: commentElement,
						commentId,
					});

					if (success) {
						hasOpenedFocusedComment.current = true;
					}
				}
			}
		}
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (!fg('cc_comments_fix_focused_comment_reopen')) {
			return () => {
				hasOpenedFocusedComment.current = false;
			};
		}
		// NOTE: hasOpenedFocusedComment is intentionally NOT reset here. This effect's
		// cleanup runs before every re-invocation (any dependency change), not just on
		// unmount. Resetting the guard here defeated its own purpose: e.g. closing the
		// comments panel then saving an unrelated footer/object comment can change
		// `commentDataResult.comment`'s or `annotationState`'s reference, re-running this
		// effect while `commentId` is unchanged — which previously reopened the panel and
		// re-scrolled to the already-handled focused comment. The reset-on-`commentId`-
		// change effect above is the correct (and only) place to clear this guard.
	}, [
		commentId,
		commentDataResult.comment,
		annotationState?.annotations,
		annotationState?.annotationsLoaded,
		showCommentsPanel,
		setCurrentlySelectedThreadKey,
		setCommentContainerToFocus,
	]);
}
